import { type ErrorResponse, isRouteErrorResponse, Link, useParams, useRouteError } from '@remix-run/react';
import { captureRemixErrorBoundaryError } from '@sentry/remix';
import { Button } from '#app/components/ui/button.tsx';
import { Icon } from '#app/components/ui/icon.tsx';
import { getErrorMessage, isNetworkError, isNetworkErrorMessage } from '#app/utils/misc.tsx';

type StatusHandler = (info: { error: ErrorResponse; params: Record<string, string | undefined> }) => JSX.Element | null;

function ErrorCard({
  icon,
  title,
  description,
  action,
}: {
  icon: 'error' | 'warn';
  title: string;
  description?: string;
  action?: JSX.Element;
}) {
  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <Icon name={icon} className="size-16 text-destructive" />
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">{title}</h1>
        {description && <p className="text-muted-foreground">{description}</p>}
      </div>
      {action ?? (
        <Button asChild variant="outline">
          <Link to="/">Go home</Link>
        </Button>
      )}
    </div>
  );
}

/**
 * A dropped fetch during navigation isn't our fault and isn't worth ejecting the
 * user to the home page over — losing your place mid-signup is worse than the
 * blip itself. Reloading retries the navigation they were already making.
 */
function OfflineErrorCard() {
  return (
    <ErrorCard
      icon="warn"
      title="You appear to be offline"
      description="Check your connection and try again."
      action={
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try again
        </Button>
      }
    />
  );
}

export function GeneralErrorBoundary({
  defaultStatusHandler = ({ error }) => (
    <ErrorCard icon="warn" title="Oops, something's not right" description={error.data} />
  ),
  statusHandlers,
  unexpectedErrorHandler = (error) => (
    <ErrorCard icon="error" title="Something went wrong on our end" description={getErrorMessage(error)} />
  ),
}: {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, StatusHandler>;
  unexpectedErrorHandler?: (error: unknown) => JSX.Element | null;
}) {
  const error = useRouteError();
  const isOffline = isRouteErrorResponse(error) ? isNetworkErrorMessage(error.data) : isNetworkError(error);

  // Don't report the user's connection dropping as an application error.
  if (!isOffline) {
    captureRemixErrorBoundaryError(error);
  }
  const params = useParams();

  if (typeof document !== 'undefined' && !isOffline) {
    console.error(error);
  }

  if (isOffline) {
    return (
      <div className="container flex items-center justify-center p-20">
        <OfflineErrorCard />
      </div>
    );
  }

  return (
    <div className="container flex items-center justify-center p-20">
      {isRouteErrorResponse(error)
        ? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
            error,
            params,
          })
        : unexpectedErrorHandler(error)}
    </div>
  );
}
